import debugFn from 'debug';
import { StoreApi } from 'zustand/vanilla';
import { BasePlaybackActions, BasePlaybackState } from '../store/base-playback-slice';

const debug = debugFn('PlaybackRegistry');

type PlaybackStore = StoreApi<BasePlaybackState & BasePlaybackActions>;

class PlaybackRegistry {
  private stores = new Set<PlaybackStore>();

  /**
   * Returns the store registered with the given playbackId, or undefined if none.
   * Used by components (e.g. drawer panel) that need to subscribe to a specific player without React context.
   */
  get(playbackId: string): PlaybackStore | undefined {
    return Array.from(this.stores).find((s) => s.getState().playbackId === playbackId);
  }

  register(store: PlaybackStore): () => void {
    const playbackId = store.getState().playbackId;

    debug('registering store for player %s, total count: %d', playbackId, this.stores.size + 1);
    this.stores.add(store);

    return () => {
      debug(
        'unregistering store for player %s, remaining count: %d',
        playbackId,
        this.stores.size - 1,
      );
      this.stores.delete(store);
    };
  }

  /**
   * Applies exclusive playback mode to all other stores based on each target store's mode.
   * When a player starts playing, each other playing player applies its own exclusivePlaybackMode:
   * - If a player's mode is 'pause', it gets paused
   * - If a player's mode is 'muted', it gets muted
   */
  applyExclusivePlaybackMode(activeStore: PlaybackStore): void {
    const activeId = activeStore.getState().playbackId;
    const _activePrefix = activeId ? `[${activeId}] ` : '';

    const stores = Array.from(this.stores).filter((store) => store !== activeStore);

    if (!stores.length) {
      return;
    }

    debug('Applying exclusive playback mode to other %d players', stores.length);

    stores.forEach((store) => {
      if (!store.getState().isPlaying) {
        // do nothing if this store is not playing
        return;
      }

      const state = store.getState();
      const targetMode = state.exclusivePlaybackMode;
      const otherId = state.playbackId;

      if (targetMode === 'pause') {
        debug('Pausing player %s', otherId);
        state.pause();
        state.unmute();
      } else if (targetMode === 'muted') {
        debug('Muting player %s', otherId);
        state.mute();
      }
    });
  }
}

export const playbackRegistry = new PlaybackRegistry();
